import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Star, Zap, Crown, Building2, ArrowRight, Phone } from 'lucide-react';

const PricingPlans = () => {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'BLDR Starter',
      price: '99',
      period: '/month',
      setupFee: '199',
      commitment: '12-month commitment',
      description: 'Essential services for new businesses getting started',
      icon: Zap,
      features: [
        'Business Entity Formation',
        'EIN & Tax Registration',
        'Registered Agent Service',
        'Basic Bookkeeping',
        'Email Support',
        '1 Consultation/month',
        'Access to AI Interview'
      ],
      popular: false
    },
    {
      name: 'BLDR Pro',
      price: '199',
      period: '/month',
      setupFee: '199',
      commitment: '12-month commitment',
      description: 'Complete business development suite for scaling',
      icon: Star,
      features: [
        'Everything in Starter',
        'Financial Management Suite',
        'Marketing Setup & Strategy',
        'CRM Implementation',
        'Priority Support',
        '4 Consultations/month',
        'Advizzu AI Assistant',
        'Analytics Dashboard'
      ],
      popular: true
    },
    {
      name: 'BLDR Elite',
      price: '299',
      period: '/month',
      setupFee: '199',
      commitment: '12-month commitment',
      description: 'Full-service business acceleration package',
      icon: Crown,
      features: [
        'Everything in Pro',
        'Legal & Compliance',
        'Sales Strategy Development',
        'CFO Advisory Services',
        'Dedicated Account Manager',
        'Unlimited Consultations',
        'Advanced Reports',
        'Marketplace Access'
      ],
      popular: false
    },
    {
      name: 'BLDR Enterprise',
      price: 'Custom',
      period: '',
      setupFee: 'Custom',
      commitment: 'Custom terms',
      description: 'Tailored solutions for larger organizations',
      icon: Building2,
      features: [
        'Everything in Elite',
        'White-label Solutions',
        'Custom Integrations',
        'Dedicated Team',
        'SLA Guarantee',
        'Custom Reporting',
        'Strategic Partnership',
        'On-site Support'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-900"
            >
              <ArrowRight className="w-5 h-5 rotate-180" />
              Back to Home
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">Cz</span>
              </div>
              <span className="font-bold text-gray-900">Companeze</span>
              <span className="text-blue-900 text-sm font-semibold">BLDR</span>
            </div>
            <a href="tel:5104224451" className="flex items-center gap-2 text-gray-600 hover:text-blue-900">
              <Phone className="w-4 h-4" />
              (510) 422-4451
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-blue-900 py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-amber-500 text-white border-0">Companeze BLDR Pro</Badge>
          <h1 className="text-4xl font-bold text-white mb-4">Simple, Transparent Pricing</h1>
          <p className="text-xl text-blue-200">
            Choose the plan that fits your business needs. All plans include the $199 evaluation fee.
          </p>
        </div>
      </section>

      {/* Pricing Grid */}
      <section className="py-16 px-6 -mt-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative bg-white overflow-hidden ${
                  plan.popular 
                    ? 'border-2 border-blue-900 shadow-xl scale-105' 
                    : 'border border-gray-200 shadow-lg'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500"></div>
                )}
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-500 text-white px-4 py-1 border-0">
                      <Star className="w-3 h-3 mr-1" /> Most Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6 pt-8">
                  <div className="w-12 h-12 bg-blue-900 rounded-xl flex items-center justify-center mb-4">
                    <plan.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{plan.name}</h3>
                  <p className="text-sm text-gray-500 mb-4">{plan.description}</p>
                  
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-gray-900">
                      {plan.price === 'Custom' ? '' : '$'}{plan.price}
                    </span>
                    <span className="text-gray-500">{plan.period}</span>
                  </div>
                  
                  <p className="text-xs text-gray-400 mb-4">
                    + ${plan.setupFee} evaluation fee • {plan.commitment}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-blue-900 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button 
                    className={`w-full ${
                      plan.popular 
                        ? 'bg-blue-900 hover:bg-blue-800 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => plan.price === 'Custom' ? window.location.href = 'tel:5104224451' : navigate('/interview')}
                  >
                    {plan.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Interview Lite CTA */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Not Sure Where to Start?</h2>
          <p className="text-gray-600 mb-6">
            Begin with our Interview Lite - a $99 assessment that provides personalized recommendations.
          </p>
          <Button 
            onClick={() => navigate('/interview')}
            className="bg-blue-900 hover:bg-blue-800 text-white px-8 py-6"
          >
            Start Interview Lite - $99 <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-900 text-blue-200 py-8 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-sm">© 2025 Companeze BLDR. All rights reserved.</p>
          <p className="mt-2 text-sm">
            <a href="tel:5104224451" className="hover:text-white">(510) 422-4451</a>
            {' • '}
            <a href="mailto:info@companeze.com" className="hover:text-white">info@companeze.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPlans;
